/**
 * Settings' section jumper, as actions on Paper's command palette.
 *
 * The jumper (`AdminSearchPicker` over `ADMIN_SECTIONS`) was the working half
 * of the old top bar's search pill: every admin section, grouped, matched on
 * its name, description, group, URL and keywords. One Paper puts ⌘K on
 * `paper-command-palette` over `PaperActionRegistry`; this keeps the jumper's
 * list and its navigation (`AdminSearchService.navigateTo`, which opens a
 * tabbable admin URL as a tab) and hands both to the registry as actions.
 *
 * Pure: no Angular, so the mapping is a plain function a spec can call.
 */
import type { PaperAction } from '@ricoh-paper/angular/shell';
import type { AdminSection } from '../core/services/admin-tabs.utils';

/** The sections the idle palette suggests before anything is typed. */
export const SUGGESTED_SECTION_URLS: readonly string[] = [
  '/estate/locations',
  '/workplace/spaces',
  '/people/directory',
  '/people/roles',
  '/governance/policies',
  '/platform/integrations',
];

/** Resolve a section string: its translation when the key resolves, else the English. */
export type SectionText = (key: string | undefined, fallback: string) => string;

/**
 * ⌘K hides what the rail hides. A section whose URL belongs to a nav item
 * (`activeIdFor`, the rail's own longest-route match) is offered only while
 * that item is in the gated nav (`visibleIds`: `visibleAdminNav` over the
 * viewer's actions, the scope, the feature flags and platform access). A
 * section the nav does not carry at all has no gate to read and stays.
 */
export function sectionGate(
  visibleIds: ReadonlySet<string>,
  activeIdFor: (url: string) => string | null,
): (section: AdminSection) => boolean {
  return (section) => {
    const id = activeIdFor(section.url);
    return id === null || visibleIds.has(id);
  };
}

/** The action id for a section. Stable across languages, so Recent survives a switch. */
export function sectionActionId(section: AdminSection): string {
  return `section:${section.url}`;
}

/**
 * The URL's words, so "integrations activity" finds `/platform/integrations/activity`
 * as the jumper did (it matched on the URL too).
 */
function urlWords(url: string): string[] {
  return url
    .split(/[/?#=&-]+/)
    .map((w) => w.trim())
    .filter(Boolean);
}

/**
 * One action per visible section. `production` drops the developer-only
 * sections, as the jumper did; `visible` drops what the rail hides
 * (`sectionGate`). Labels, descriptions and group headings are resolved now,
 * in the active language: call again when it, or the gate, changes.
 */
export function sectionActions(
  sections: readonly AdminSection[],
  text: SectionText,
  navigate: (section: AdminSection) => void,
  production: boolean,
  visible: (section: AdminSection) => boolean = () => true,
): PaperAction[] {
  return sections
    .filter((s) => (!s.devOnly || !production) && visible(s))
    .map((section) => {
      const label = text(section.labelKey, section.label);
      const description = section.description
        ? text(section.descriptionKey, section.description)
        : undefined;
      const group = section.group ? text(section.groupKey, section.group) : undefined;
      const keywords = [
        ...(section.keywords ?? []),
        ...(description ? [description] : []),
        ...(group ? [group] : []),
        // The English name too, so an admin who knows the page by its English
        // name still finds it in another language.
        ...(label !== section.label ? [section.label] : []),
        ...urlWords(section.url),
      ];
      return {
        id: sectionActionId(section),
        label,
        description,
        group,
        icon: section.icon,
        keywords,
        suggested: SUGGESTED_SECTION_URLS.includes(section.url),
        run: () => navigate(section),
      } satisfies PaperAction;
    });
}
